import { Banner } from "@/components/flexkit/Banner";
import { formatShortDate } from "@/format/time";
import { useGetBillingInfo } from "@/queries/organization-queries";
import { Timestamp } from "@bufbuild/protobuf";
import { PaymentFailureState } from "gitpod-next-api/gitpod/v1/billing_pb";
import { useFlag } from "@/hooks/use-flag";
import { Flags } from "@/feature-flags";
import { type FC } from "react";
import { useNavigate } from "react-router-dom";

export const PaymentFailureBanner: FC = () => {
    const { value: featureFlagEnabled } = useFlag(Flags.EnableFailedPaymentReconciler, false);
    const { data: billingInfo, isLoading } = useGetBillingInfo();
    const navigate = useNavigate();

    if (isLoading) {
        // Don't display any stand-in for banners
        return null;
    }

    if (!featureFlagEnabled) {
        return null;
    }

    const paymentFailure = billingInfo?.paymentFailure;

    if (!paymentFailure) {
        return null;
    }

    // Only show banner for IN_GRACE or SUSPENDED states
    if (
        paymentFailure.state !== PaymentFailureState.IN_GRACE &&
        paymentFailure.state !== PaymentFailureState.SUSPENDED
    ) {
        return null;
    }

    if (!paymentFailure.gracePeriodEndsAt) {
        return null;
    }

    const endDate = new Timestamp(paymentFailure.gracePeriodEndsAt).toDate();
    const now = new Date();
    const isGracePeriodExpired = endDate <= now;

    // SUSPENDED state or grace period has expired
    if (isGracePeriodExpired || paymentFailure.state === PaymentFailureState.SUSPENDED) {
        const formattedEndDate = formatShortDate(endDate, true);

        return (
            <Banner
                className="rounded-none shadow-none"
                variant="warning"
                text={`Your subscription was suspended on ${formattedEndDate}. This organization has been moved to the Free Tier.`}
                action={{
                    text: "View Billing",
                    onClick: () => navigate("/settings/billing"),
                    "data-tracking-id": "payment-suspended-banner-cta",
                }}
                role="alert"
                aria-live="polite"
                data-testid="payment-suspended-banner"
            />
        );
    }

    // IN_GRACE state - grace period is still active
    const formattedEndDate = formatShortDate(endDate, true);

    return (
        <Banner
            className="rounded-none shadow-none"
            variant="danger"
            text={`Your payment didn't go through. Please update your payment method by ${formattedEndDate} to avoid moving this organization to the Free Tier.`}
            action={{
                text: "Update Payment",
                onClick: () => navigate("/settings/billing"),
                "data-tracking-id": "payment-grace-period-banner-cta",
            }}
            role="alert"
            aria-live="assertive"
            data-testid="payment-grace-period-banner"
        />
    );
};
